import { snoozeConversation } from '@/services/snoozeService'
import { listMacros, type MacroAction } from '@/services/macrosService'
import {
  toggleConversationStatus, muteConversation, assignAgent, assignTeam,
  fetchConversationLabels, setConversationLabels, fetchConversation,
} from '@/services/conversationActionsService'
import { postMessage } from '@/services/messagesService'
import { useAdmin } from '@/composables/useAdmin'
import { logMacroExecution } from '@/services/macroExecutionLogService'

/** Dados da conversa/contato necessários pelas ações de webhook (não passam por conversationActionsService). */
export interface MacroActionContext {
  contactPhone?: string | null
  contactName?: string | null
  contactEmail?: string | null
  teamId?: number | string | null
  teamName?: string | null
  conversationStatus?: string | null
  contactId?: number | string | null
  assigneeId?: number | string | null
  assigneeName?: string | null
  inboxId?: number | string | null
  inboxName?: string | null
  accountId?: number | string | null
  labels?: string[]
  /** Conversa completa (formato bruto da API do Chatwoot) — usada como payload quando o corpo do webhook customizado está vazio. */
  rawConversation?: unknown
  /** Identidade da macro/executor, só para o histórico de execuções (ver macroExecutionLogService.ts). Ausente = não loga. */
  macroId?: string | null
  macroName?: string | null
  executedById?: number | string | null
  executedByName?: string | null
}

export interface MacroActionResult {
  label: string
  ok: boolean
  msg: string
}

export const MACRO_ACTION_LABELS: Record<string, string> = {
  resolve_conversation:     'Resolver conversa',
  reopen_conversation:      'Reabrir conversa',
  snooze_conversation:      'Adiar conversa',
  mute_conversation:        'Silenciar conversa',
  assign_team:              'Atribuir time',
  assign_agent:             'Atribuir agente',
  add_label:                'Adicionar etiqueta',
  remove_label:             'Remover etiqueta',
  send_message:             'Enviar mensagem',
  add_private_note:         'Nota privada',
  send_email_transcript:    'Enviar transcrição e-mail',
  send_webhook_event:       'Webhook',
  send_webhook_event_form:  'Webhook com Formulário',
}

async function runWebhook(url: string, customHeaders: Array<{ name: string; value: string }>, payload: Record<string, any>): Promise<void> {
  const reqHdrs: Record<string, string> = { 'Content-Type': 'application/json' }
  for (const h of customHeaders) { if (h.name?.trim()) reqHdrs[h.name.trim()] = h.value }
  const r = await fetch(url, { method: 'POST', headers: reqHdrs, body: JSON.stringify(payload) })
  if (!r.ok) throw new Error(`Webhook: ${r.status}`)
}

/** ConvDrawer e FloatChatWindow guardavam assign_team/assign_agent como `{id}` ou como id cru — normaliza os dois. */
function resolveActionId(v: any): number | null {
  if (v == null) return null
  return typeof v === 'object' ? Number(v.id) : Number(v)
}

/**
 * add_label/remove_label vinham em dois formatos diferentes em ConvDrawer (`action_params[0]`
 * como array) e FloatChatWindow (`action_params` inteiro como array de labels) — aceita ambos.
 */
function normalizeLabelParams(params: any[]): string[] {
  return params
    .flatMap(p => Array.isArray(p) ? p : [p])
    .map(x => typeof x === 'string' ? x : String(x))
    .filter(Boolean)
}

/** Monta o mapa de variáveis {{key}} disponíveis no corpo customizado de send_webhook_event. */
function buildWebhookVars(convId: number, context: MacroActionContext): Record<string, any> {
  return {
    conversation_id:     convId,
    conversation_status: context.conversationStatus ?? '',
    contact_id:          context.contactId ?? '',
    contact_name:        context.contactName ?? '',
    contact_email:       context.contactEmail ?? '',
    contact_phone:       context.contactPhone ?? '',
    assignee_id:         context.assigneeId ?? '',
    assignee_name:       context.assigneeName ?? '',
    team_id:             context.teamId ?? '',
    team_name:           context.teamName ?? '',
    inbox_id:            context.inboxId ?? '',
    inbox_name:          context.inboxName ?? '',
    account_id:          context.accountId ?? '',
    labels:              JSON.stringify(context.labels ?? []),
  }
}

function substituteTemplate(template: string, vars: Record<string, any>): string {
  return template.replace(/\{\{(\w+)\}\}/g, (_, k) => vars[k] !== undefined ? String(vars[k]) : `{{${k}}}`)
}

/**
 * Executa uma ação de macro sobre uma conversa. Reaproveita conversationActionsService
 * para as ações que mutam a conversa no Chatwoot; webhooks continuam sendo chamados
 * direto (URL é definida pelo usuário na própria macro, fora do Chatwoot).
 */
export async function runMacroAction(
  action: MacroAction,
  convId: number,
  token: string,
  context: MacroActionContext,
  formValues?: Record<string, string>,
): Promise<MacroActionResult> {
  const label = MACRO_ACTION_LABELS[action.action_name] || action.action_name

  function finish(result: MacroActionResult): MacroActionResult {
    if (context.macroId) {
      logMacroExecution(token, {
        macroId: context.macroId, macroName: context.macroName || '', conversationId: convId,
        actionName: action.action_name, actionLabel: result.label, ok: result.ok, msg: result.msg,
        executedById: context.executedById ?? null, executedByName: context.executedByName ?? null,
      })
    }
    return result
  }

  try {
    const p = action.action_params
    switch (action.action_name) {
      case 'resolve_conversation': {
        const status = await toggleConversationStatus(convId, token, 'resolved')
        if (status === null) throw new Error('Erro ao resolver conversa')
        break
      }
      case 'reopen_conversation': {
        const status = await toggleConversationStatus(convId, token, 'open')
        if (status === null) throw new Error('Erro ao reabrir conversa')
        break
      }
      case 'snooze_conversation':
        await snoozeConversation(convId, token)
        break
      case 'mute_conversation': {
        const ok = await muteConversation(convId, token)
        if (!ok) throw new Error('Erro ao silenciar conversa')
        break
      }
      case 'assign_team': {
        const ok = await assignTeam(convId, token, resolveActionId(p[0]))
        if (!ok) throw new Error('Erro ao atribuir time')
        break
      }
      case 'assign_agent': {
        const ok = await assignAgent(convId, token, resolveActionId(p[0]))
        if (!ok) throw new Error('Erro ao atribuir agente')
        break
      }
      case 'add_label': {
        const result = await setConversationLabels(convId, token, normalizeLabelParams(p))
        if (result === null) throw new Error('Erro ao adicionar etiqueta')
        break
      }
      case 'remove_label': {
        const curr = await fetchConversationLabels(convId, token)
        const toRemove = normalizeLabelParams(p)
        const result = await setConversationLabels(convId, token, curr.filter(l => !toRemove.includes(l)))
        if (result === null) throw new Error('Erro ao remover etiqueta')
        break
      }
      case 'send_message': {
        const msg = await postMessage(convId, token, p[0]?.content ?? (typeof p[0] === 'string' ? p[0] : ''), false)
        if (msg === null) throw new Error('Erro ao enviar mensagem')
        break
      }
      case 'add_private_note': {
        const msg = await postMessage(convId, token, p[0]?.content ?? (typeof p[0] === 'string' ? p[0] : ''), true)
        if (msg === null) throw new Error('Erro ao adicionar nota privada')
        break
      }
      case 'send_webhook_event': {
        const url          = String(p[0] ?? '')
        const bodyTemplate = String(p[1] ?? '')
        const customHdrs   = Array.isArray(p[2]) ? p[2] as Array<{ name: string; value: string }> : []
        if (!url) throw new Error('URL não definida')

        let payload: Record<string, any>
        if (bodyTemplate.trim()) {
          const vars = buildWebhookVars(convId, context)
          const replaced = substituteTemplate(bodyTemplate, vars)
          try { payload = JSON.parse(replaced) }
          catch { throw new Error('Corpo do webhook não é um JSON válido após substituição das variáveis') }
        } else {
          payload = (context.rawConversation as Record<string, any>) ?? { conversation_id: convId }
        }

        await runWebhook(url, customHdrs, payload)
        break
      }
      case 'send_webhook_event_form': {
        const cfg        = p[0] ?? {}
        const url        = typeof cfg === 'object' ? String(cfg.url ?? '') : ''
        const customHdrs = Array.isArray(cfg.headers) ? cfg.headers as Array<{ name: string; value: string }> : []
        if (!url) throw new Error('URL não definida')
        await runWebhook(url, customHdrs, {
          conversation_id: convId,
          contact_name:    context.contactName  ?? '',
          contact_phone:   context.contactPhone ?? '',
          contact_email:   context.contactEmail ?? '',
          team_id:         context.teamId       ?? '',
          team_name:       context.teamName     ?? '',
          ...(formValues ?? {}),
        })
        break
      }
      default:
        return finish({ label, ok: false, msg: 'Ação não suportada' })
    }
    return finish({ label, ok: true, msg: '' })
  } catch (e: any) {
    return finish({ label, ok: false, msg: e?.message || 'Erro desconhecido' })
  }
}

export interface ResolveWithMacroOutcome {
  ranMacro: boolean
  ok: boolean
  status: string | null
  macroName?: string
  results?: MacroActionResult[]
}

/**
 * Ponto único do botão "Resolver" nas 3 telas de chat (drawer, janela flutuante, mobile).
 * Se "Resolver com Macro" estiver habilitado em Configurações Gerais e uma macro ativa
 * estiver selecionada, executa a macro inteira (que deve conter resolve_conversation, se
 * o objetivo for realmente resolver) em vez do toggle_status direto.
 */
export async function resolveConversationSmart(
  convId: number,
  token: string,
  baseContext: MacroActionContext,
): Promise<ResolveWithMacroOutcome> {
  const { globalSettings } = useAdmin()
  const macroId = globalSettings.value.resolveWithMacroEnabled ? globalSettings.value.resolveWithMacroId : ''

  if (macroId) {
    const macros = await listMacros(token)
    const macro = macros.find(m => m.id === macroId && m.is_active)
    if (macro) {
      const needsLiveContext = macro.actions.some(a => a.action_name === 'send_webhook_event' || a.action_name === 'send_webhook_event_form')
      let context = baseContext
      if (needsLiveContext) {
        const data = await fetchConversation(convId, token)
        if (data) {
          context = {
            ...baseContext,
            contactEmail: data?.meta?.sender?.email ?? baseContext.contactEmail,
            inboxId:      data?.inbox_id ?? null,
            inboxName:    data?.channel ?? null,
            rawConversation: data,
          }
        }
      }
      context = { ...context, macroId: macro.id, macroName: macro.name }
      const results: MacroActionResult[] = []
      for (const action of macro.actions) {
        results.push(await runMacroAction(action, convId, token, context))
      }
      return { ranMacro: true, ok: results.every(r => r.ok), status: 'resolved', macroName: macro.name, results }
    }
  }

  const status = await toggleConversationStatus(convId, token, 'resolved')
  return { ranMacro: false, ok: status !== null, status }
}
